
import { useEffect, useState } from 'react';

interface PageTitleProps {
  title: string;
  subtitle?: string;
}

export default function PageTitle({ title, subtitle }: PageTitleProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="relative bg-gradient-to-br from-[#fafafa] via-white to-[#f8f6f0] border-b border-[#e5e5e5]/50 overflow-hidden">
      {/* 装飾的な背景パターン */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-[#d4af37]/5 to-transparent"></div>
        <div className="absolute top-0 right-0 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 bg-gradient-to-bl from-[#d4af37]/10 to-transparent rounded-full blur-3xl transform translate-x-32 sm:translate-x-40 md:translate-x-48 -translate-y-32 sm:-translate-y-40 md:-translate-y-48"></div>
        <div className="absolute bottom-0 left-0 w-48 sm:w-56 md:w-64 h-48 sm:h-56 md:h-64 bg-gradient-to-tr from-[#1a2942]/5 to-transparent rounded-full blur-2xl transform -translate-x-24 sm:-translate-x-28 md:-translate-x-32 translate-y-24 sm:translate-y-28 md:translate-y-32"></div>
      </div>

      {/* メインコンテンツ */}
      <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-12 md:py-16">
        <div className="text-center">
          {/* 装飾的なライン */}
          <div className="flex items-center justify-center mb-4 sm:mb-6 md:mb-8">
            <div className="w-8 sm:w-12 md:w-16 h-px bg-gradient-to-r from-transparent to-[#d4af37]"></div>
            <div className="mx-3 sm:mx-4 w-2 sm:w-2.5 md:w-3 h-2 sm:h-2.5 md:h-3 bg-[#d4af37] rounded-full shadow-lg"></div>
            <div className="w-8 sm:w-12 md:w-16 h-px bg-gradient-to-l from-transparent to-[#d4af37]"></div>
          </div>

          {/* メインタイトル */}
          <h1 
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 md:mb-6 transition-all duration-1000 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
            style={{
              fontFamily: '"Playfair Display", "Noto Serif JP", serif',
              background: 'linear-gradient(135deg, #1a2942 0%, #2c3e50 30%, #d4af37 70%, #f1c40f 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 4px 8px rgba(26, 41, 66, 0.1)'
            }}
          >
            {title}
          </h1>

          {/* カスタムサブタイトルまたはデフォルトの英語サブタイトル */}
          <div 
            className={`transition-all duration-1000 delay-300 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            {subtitle ? (
              <p className="text-sm sm:text-base md:text-lg text-[#666] max-w-2xl mx-auto font-['Noto_Serif_JP',serif] mb-4 sm:mb-6 md:mb-8">
                {subtitle}
              </p>
            ) : (
              <p 
                className="text-sm sm:text-base md:text-lg text-[#6b7280] tracking-[0.1em] sm:tracking-[0.15em] md:tracking-[0.2em] font-light mb-4 sm:mb-6 md:mb-8"
                style={{fontFamily: '"Playfair Display", serif'}}
              >
                {title === '協会概要' && 'ABOUT OUR ASSOCIATION'}
                {title === '理念と使命' && 'PHILOSOPHY & MISSION'}
                {title === '沿革・歴史' && 'HISTORY & HERITAGE'}
                {title === '組織体制' && 'ORGANIZATION STRUCTURE'}
                {title === '主な活動' && 'MAIN ACTIVITIES'}
                {title === 'アクセス・所在地' && 'ACCESS & LOCATION'}
              </p>
            )}
          </div>

          {/* 装飾的な下部ライン */}
          <div 
            className={`flex items-center justify-center transition-all duration-1000 delay-500 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="w-6 sm:w-7 md:w-8 h-px bg-gradient-to-r from-transparent to-[#d4af37]/60"></div>
            <div className="mx-2 sm:mx-3 flex space-x-1">
              <div className="w-1 h-1 bg-[#d4af37] rounded-full"></div>
              <div className="w-1 h-1 bg-[#d4af37]/60 rounded-full"></div>
              <div className="w-1 h-1 bg-[#d4af37]/30 rounded-full"></div>
            </div>
            <div className="w-6 sm:w-7 md:w-8 h-px bg-gradient-to-l from-transparent to-[#d4af37]/60"></div>
          </div>
        </div>
      </div>

      {/* 下部のグラデーション境界 */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#d4af37]/20 via-[#d4af37]/60 to-[#d4af37]/20"></div>
    </div>
  );
}
